import { useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/apiClient";
import {
  updateDokumentStatus,
  type AngebotNachfassDokument,
  type AngebotNachfassGroup,
} from "@/lib/dokumente";
import { toast } from "@/lib/toast";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  formatAngebotCurrency,
  formatAngebotOpenMeta,
  partitionNachfassAngebote,
  resolveAngebotAgeThreshold,
} from "@/pages/dokumente/angebotNachfassLogic";
import { DocumentMailDialog } from "@/pages/dokumente/DocumentMailDialog";

export function AngebotNachfassModal({
  open,
  group,
  onClose,
  onChanged,
  onOpenDokument,
  zIndexClass,
}: {
  open: boolean;
  group: AngebotNachfassGroup | null;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  onOpenDokument?: (id: number) => void;
  zIndexClass?: string;
}) {
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [mailDocId, setMailDocId] = useState<number | null>(null);

  if (!group) return null;

  const { overdue: overdueOffers, followedUp: followedOffers, newer: newerOffers } =
    partitionNachfassAngebote(group.angebote);
  const hasOtherOffers = newerOffers.length > 0 || followedOffers.length > 0;

  async function handleReject(doc: AngebotNachfassDokument) {
    const nummer = doc.display_number || doc.dokument_nummer || `#${doc.id}`;
    if (
      !window.confirm(
        `Angebot ${nummer} als abgelehnt markieren? Dies kann Auswirkungen auf verknüpfte Projekte haben.`,
      )
    ) {
      return;
    }
    setRejectingId(doc.id);
    try {
      await updateDokumentStatus(doc.id, "abgelehnt");
      toast.success(`Angebot ${nummer} als abgelehnt markiert.`);
      await onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setRejectingId(null);
    }
  }

  return (
    <>
      <Dialog
        open={open && mailDocId == null}
        onClose={onClose}
        title={`Angebote · ${group.name}`}
        size="2xl"
        zIndexClass={zIndexClass}
        footer={<DialogCancelButton onClose={onClose} variant="ghost" />}
      >
        <div className="space-y-4">
          <section className="rounded-ios border border-separator bg-fill-tertiary/40 px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
              Ansprechpartner
            </p>
            <p className="mt-1 text-base font-semibold text-label">{group.name}</p>
            <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-label-secondary">E-Mail</dt>
                <dd className="text-label">
                  {group.email ? (
                    <a className="text-accent hover:underline" href={`mailto:${group.email}`}>
                      {group.email}
                    </a>
                  ) : (
                    <span className="text-label-tertiary">—</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-label-secondary">Telefon</dt>
                <dd className="text-label">
                  {group.telefon ? (
                    <a className="text-accent hover:underline" href={`tel:${group.telefon}`}>
                      {group.telefon}
                    </a>
                  ) : (
                    <span className="text-label-tertiary">—</span>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <div className="space-y-4">
            {overdueOffers.length > 0 ? (
              <AngebotNachfassOfferSection
                title={
                  hasOtherOffers
                    ? `Überfällig (${overdueOffers.length})`
                    : `Offene Angebote (${overdueOffers.length})`
                }
                angebote={overdueOffers}
                rejectingId={rejectingId}
                onOpenDokument={onOpenDokument}
                onResend={setMailDocId}
                onReject={(doc) => void handleReject(doc)}
              />
            ) : null}
            {followedOffers.length > 0 ? (
              <AngebotNachfassOfferSection
                title={`Nachgefasst (${followedOffers.length})`}
                angebote={followedOffers}
                rejectingId={rejectingId}
                onOpenDokument={onOpenDokument}
                onResend={setMailDocId}
                onReject={(doc) => void handleReject(doc)}
              />
            ) : null}
            {newerOffers.length > 0 ? (
              <AngebotNachfassOfferSection
                title={
                  overdueOffers.length > 0 || followedOffers.length > 0
                    ? `Weitere offene Angebote (${newerOffers.length})`
                    : `Offene Angebote (${newerOffers.length})`
                }
                angebote={newerOffers}
                rejectingId={rejectingId}
                onOpenDokument={onOpenDokument}
                onResend={setMailDocId}
                onReject={(doc) => void handleReject(doc)}
              />
            ) : null}
          </div>
        </div>
      </Dialog>

      {mailDocId != null ? (
        <DocumentMailDialog
          open
          docId={mailDocId}
          vorlage="angebot_nachfass"
          defaultMarkVersendet={false}
          zIndexClass="z-[80]"
          onClose={() => setMailDocId(null)}
          onSent={async () => {
            toast.success("Nachfass-E-Mail wurde versendet.");
            setMailDocId(null);
            await onChanged();
          }}
          onError={(message) => toast.error(message)}
        />
      ) : null}
    </>
  );
}

function AngebotNachfassOfferSection({
  title,
  angebote,
  rejectingId,
  onOpenDokument,
  onResend,
  onReject,
}: {
  title: string;
  angebote: AngebotNachfassDokument[];
  rejectingId: number | null;
  onOpenDokument?: (id: number) => void;
  onResend: (id: number) => void;
  onReject: (doc: AngebotNachfassDokument) => void;
}) {
  if (angebote.length === 0) return null;
  return (
    <section>
      <h3 className="text-sm font-semibold text-label">{title}</h3>
      <ul className="mt-2 divide-y divide-separator rounded-ios border border-separator">
        {angebote.map((doc) => {
          const threshold = resolveAngebotAgeThreshold(doc.age_days);
          const openMeta = formatAngebotOpenMeta(doc);
          return (
            <li key={doc.id} className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="font-semibold text-accent hover:underline"
                    onClick={() => onOpenDokument?.(doc.id)}
                  >
                    {doc.display_number || doc.dokument_nummer || `Angebot #${doc.id}`}
                  </button>
                  {threshold ? (
                    <TimesheetBadge compact className={threshold.className}>
                      {threshold.label}
                    </TimesheetBadge>
                  ) : (
                    <TimesheetBadge compact subtle>
                      ≤ 1 Woche
                    </TimesheetBadge>
                  )}
                  {openMeta && doc.nachgefasst_am ? (
                    <TimesheetBadge compact className="bg-sky-600 text-white">
                      {doc.nachgefasst_art === "angebot_nachfass" ? "Nachgefragt" : "Erneut gesendet"}
                    </TimesheetBadge>
                  ) : null}
                </div>
                <p className="truncate text-sm text-label-secondary">
                  {[doc.kunde_name, doc.betreff].filter(Boolean).join(" · ") || "—"}
                </p>
                <p className="text-xs text-label-tertiary">
                  {formatAngebotCurrency(doc.summe_netto ?? doc.summe_brutto, doc.waehrung || "EUR")}
                  {openMeta ? ` · ${openMeta}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => onResend(doc.id)}>
                  Erneut senden
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  loading={rejectingId === doc.id}
                  disabled={rejectingId != null}
                  onClick={() => onReject(doc)}
                >
                  Ablehnen
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
